var _createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

import { extendObservable, runInAction, observable, computed } from 'mobx';
import createRouteStateTreeNode from './createRouteStateTreeNode';
import createRoute from './createRoute';
import RouterStateTree from './RouterStateTree';
import qs from 'querystring';
import areRoutesEqual from './util/areRoutesEqual';

var RouterStore = function () {
  // Keep a list of activated nodes so we can track differences when transitioning to a new state.
  function RouterStore(root) {
    var _this = this;

    _classCallCheck(this, RouterStore);

    this.nextKey = 0;

    this.getNextKey = function () {
      var key = _this.nextKey;
      runInAction(function () {
        _this.nextKey++;
      });
      return '' + key;
    };

    this.state = new RouterStateTree(root);
    this.cancelledSequence = -1;
    extendObservable(this, {
      location: {},
      params: observable.map({}),
      cache: observable.map(_defineProperty({}, root.value.key, root)),
      routes: observable.array([]),
      prevRoutes: observable.array([]),
      error: null
    });
  } // Create a map of all nodes in tree so we can perform faster lookup.
  // Instances should be exactly the same as in state tree.


  _createClass(RouterStore, [{
    key: 'getNextRoutes',

    /* Queries */
    // Returns a list of the next routes from the matched path.
    // If the route is not currently active or has changed, then it will be created from factory function.
    value: function getNextRoutes(path, location) {
      var _this2 = this;

      var query = getQueryParams(location);
      return path.map(function (element) {
        var matchedQueryParams = _this2.getMatchedQueryParams(element.node, query);

        var newRoute = createRoute(element.node, element.parentUrl, element.segment, element.params, query);

        var existingRoute = _this2.routes.find(function (x) {
          return areRoutesEqual(x, newRoute);
        });

        if (existingRoute) {
          return existingRoute;
        } else {
          return observable(createRoute(element.node, element.parentUrl, element.segment, element.params, matchedQueryParams));
        }
      });
    }
  }, {
    key: 'getNode',
    value: function getNode(key) {
      var x = this.cache.get(key);

      if (x) {
        var _node = x.node;
        return _node;
      } else {
        return null;
      }
    }
  }, {
    key: 'getNodeUnsafe',
    value: function getNodeUnsafe(key) {
      var x = this.getNode(key);

      if (!x) {
        throw new Error('Cannot find node with key ' + key);
      } else {
        return x;
      }
    }
  }, {
    key: 'getRoute',
    value: function getRoute(key) {
      var x = this.routes.find(function (route) {
        return route.node.value.key === key;
      });

      if (x) {
        return x;
      } else {
        return null;
      }
    }
  }, {
    key: 'getRouteUnsafe',
    value: function getRouteUnsafe(key) {
      var x = this.getRoute(key);

      if (x) {
        return x;
      } else {
        throw new Error('Cannot find route with key ' + key);
      }
    }
  }, {
    key: 'getParams',
    value: function getParams(key) {
      var params = this.params.get(key);

      if (params) {
        return params;
      } else {
        return null;
      }
    }
    /* Mutations */

  }, {
    key: 'replaceChildren',
    value: function replaceChildren(node, children) {
      var _this3 = this;

      node.children.replace(children.map(function (x) {
        return _this3._createNode(node, x);
      }));
    }
  }, {
    key: 'updateError',
    value: function updateError(err) {
      var _this4 = this;

      runInAction(function () {
        _this4.error = err;
      });
    }
  }, {
    key: 'updateRoutes',
    value: function updateRoutes(routes) {
      var _this5 = this;

      runInAction(function () {
        _this5.prevRoutes.replace(_this5.routes.slice());

        _this5.routes.replace(routes); // Update params


        _this5.params.clear();

        _this5.routes.forEach(function (route) {
          _this5.params.set(route.node.value.key, route.params);
        });
      });
    }
  }, {
    key: 'updateLocation',
    value: function updateLocation(nextLocation) {
      var _this6 = this;

      runInAction(function () {
        _this6.location = nextLocation;
      });
    }
  }, {
    key: 'clearPrevRoutes',
    value: function clearPrevRoutes() {
      var _this7 = this;

      runInAction(function () {
        _this7.prevRoutes.replace([]);
      });
    }
  }, {
    key: 'cancel',
    value: function cancel(navigation) {
      if (navigation) {
        this.cancelledSequence = navigation.sequence;
      }
    }
  }, {
    key: 'isCancelled',
    value: function isCancelled(navigation) {
      return navigation ? navigation.sequence <= this.cancelledSequence : false;
    }
  }, {
    key: 'getMatchedQueryParams',
    value: function getMatchedQueryParams(node, query) {
      return Object.keys(query).filter(function (key) {
        return node.value.query.includes(key);
      }).reduce(function (acc, key) {
        acc[key] = query[key];
        return acc;
      }, {});
    }
    /* Private helpers */

  }, {
    key: '_createNode',
    value: function _createNode(parent, config) {
      var node = createRouteStateTreeNode(config, parent.value.getContext, this.getNextKey);

      this._storeInCache(parent, node);

      return node;
    }
  }, {
    key: '_storeInCache',
    value: function _storeInCache(parent, node) {
      var _this8 = this;

      this.cache.set(node.value.key, {
        node: node,
        parent: parent
      });
      node.children.forEach(function (child) {
        return _this8._storeInCache(node, child);
      });
    }
  }]);

  return RouterStore;
}();

function getQueryParams(location) {
  return location.search != null ? qs.parse(location.search.substr(1)) : {};
}

export default RouterStore;